import type { TemplateTask, TemplatePhase } from './templateManagementService';

export interface TaskFieldDiff {
  field: string;
  old: string;
  new: string;
}

export interface TaskDiffEntry {
  title: string;
  fields: TaskFieldDiff[];
}

export interface TaskListDiff {
  added: string[];
  removed: string[];
  modified: TaskDiffEntry[];
}

export interface PhaseDiff {
  added: string[];
  removed: string[];
  reordered: boolean;
}

function normalizeTitle(title: string): string {
  return (title || '').trim().toLowerCase();
}

function stringify(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

/**
 * Compute task-level diff between baseline and current task lists.
 */
export function diffTaskLists(
  baseline: TemplateTask[],
  current: TemplateTask[],
): TaskListDiff {
  const baseMap = new Map<string, TemplateTask>();
  for (const t of baseline) {
    const key = normalizeTitle(t.title);
    if (key) baseMap.set(key, t);
  }

  const currMap = new Map<string, TemplateTask>();
  for (const t of current) {
    const key = normalizeTitle(t.title);
    if (key) currMap.set(key, t);
  }

  const added: string[] = [];
  const removed: string[] = [];
  const modified: TaskDiffEntry[] = [];

  for (const [key, task] of baseMap) {
    if (!currMap.has(key)) removed.push(task.title);
  }

  const compareFields: Array<keyof TemplateTask> = [
    'description', 'priority', 'due_date_offset_days', 'assignee_role',
    'is_court_mandated', 'estimated_hours', 'estimated_cost',
  ];

  for (const [key, task] of currMap) {
    const prev = baseMap.get(key);
    if (!prev) {
      added.push(task.title);
      continue;
    }

    const fields: TaskFieldDiff[] = [];
    for (const field of compareFields) {
      const oldVal = stringify(prev[field]);
      const newVal = stringify(task[field]);
      if (oldVal !== newVal) {
        fields.push({ field, old: oldVal || '(empty)', new: newVal || '(empty)' });
      }
    }

    // Compare forms
    const oldForms = (prev.required_forms || []).map(f => f.form_number).join(', ');
    const newForms = (task.required_forms || []).map(f => f.form_number).join(', ');
    if (oldForms !== newForms) {
      fields.push({ field: 'required_forms', old: oldForms || 'none', new: newForms || 'none' });
    }

    if (fields.length > 0) {
      modified.push({ title: task.title, fields });
    }
  }

  return { added, removed, modified };
}

/**
 * Compute phase-level diff.
 */
export function diffPhases(
  baseline: TemplatePhase[],
  current: TemplatePhase[],
): PhaseDiff {
  const baseNames = baseline.map(p => p.name);
  const currNames = current.map(p => p.name);

  const added = currNames.filter(n => !baseNames.includes(n));
  const removed = baseNames.filter(n => !currNames.includes(n));

  // Check reordering — compare names that exist in both
  const commonBase = baseNames.filter(n => currNames.includes(n));
  const commonCurr = currNames.filter(n => baseNames.includes(n));
  const reordered = commonBase.join(',') !== commonCurr.join(',');

  return { added, removed, reordered };
}

/**
 * Format a task list diff into a summary string.
 */
export function formatDiffSummary(diff: TaskListDiff): string {
  const parts: string[] = [];
  if (diff.added.length > 0) parts.push(`+${diff.added.length} added`);
  if (diff.removed.length > 0) parts.push(`-${diff.removed.length} removed`);
  if (diff.modified.length > 0) parts.push(`~${diff.modified.length} modified`);
  return parts.length > 0 ? parts.join(', ') : 'No task changes';
}

/**
 * Flatten phases into a flat task list for diffing.
 */
export function flattenPhaseTasks(phases: TemplatePhase[]): TemplateTask[] {
  const tasks: TemplateTask[] = [];
  for (const phase of phases) {
    for (const task of phase.tasks || []) {
      tasks.push(task);
    }
  }
  return tasks;
}
